import React, { useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import "./EarningsBreakdown.css";

const EarningsBreakdown = ({
  booking = {},
  isExpanded: controlledIsExpanded,
  onToggle: controlledOnToggle,
  extraKm = 0,
  includedKm = 0,
  extraFarePerKm = 0,
  extraKmCharge = 0,
  waitingTime = 0,
  waitingCharge = 0,
  waitingChargePerMinute = 0,
  baseDue = 0,
  totalCollectAmount = 0,
}) => {
  const [localIsExpanded, setLocalIsExpanded] = useState(false);
  const isExpanded = controlledIsExpanded !== undefined ? controlledIsExpanded : localIsExpanded;
  const toggleExpand = controlledOnToggle || (() => setLocalIsExpanded(!localIsExpanded));

  // Use commission breakdown from API if available, otherwise fall back to individual fields
  const commissionBreakdown = booking.commissionBreakdown;
  const isRideCompleted = booking.status === "completed";

  let totalFare = booking.totalAmount || 0;
  let adminCommission = booking.admin_commission || 0;
  let commissionRate = booking.commission_rate || 10;
  let gstRate = booking.taxPercentage || 18;
  let gstAmount = booking.gst_amount || 0;
  let vendorCommission = booking.vendor_commission || 0;
  let driverEarnings = booking.driver_earnings !== undefined
    ? booking.driver_earnings
    : totalFare - adminCommission - gstAmount - vendorCommission;

  // First, check if there's locked breakdown data from the API (for assigned rides)
  if (commissionBreakdown && commissionBreakdown.lockedBreakdown && !isRideCompleted) {
    const lockedData = commissionBreakdown.lockedBreakdown;
    commissionRate = lockedData.adminCommission || booking.commission_rate || 10;
    gstRate = lockedData.gstPercentage || booking.taxPercentage || 18;

    // Calculate commission and GST from total fare using locked rates
    adminCommission = Math.round((totalFare * commissionRate) / 100);
    gstAmount = Math.round((totalFare * gstRate) / 100);
    vendorCommission = 0;

    // Calculate driver earnings
    driverEarnings = totalFare - adminCommission - gstAmount;
  }
  // Otherwise use commission breakdown values if available
  else if (commissionBreakdown && commissionBreakdown.summary && isRideCompleted) {
    totalFare = booking.totalAmount || 0;
    adminCommission = commissionBreakdown.summary.adminCommissionTotal || 0;
    gstAmount = commissionBreakdown.summary.gstAmount || 0;
    vendorCommission = commissionBreakdown.summary.vendorCommissionTotal || 0;
    driverEarnings = commissionBreakdown.summary.driverEarnings || 0;
    commissionRate = commissionBreakdown.metadata.commissionRate || 10;
  }
  // If no breakdown data but ride isn't completed, calculate estimated values
  else if (!isRideCompleted && adminCommission === 0 && gstAmount === 0) {
    adminCommission = Math.round((totalFare * commissionRate) / 100);
    gstAmount = Math.round((totalFare * gstRate) / 100);
    driverEarnings = totalFare - adminCommission - gstAmount - vendorCommission;
  }

  // Extract ride details for summary section
  const baseFare = booking.baseAmount || 0;
  const promoDiscount = booking.couponDiscountAmount || 0;
  const selectedAddOns = booking.selectedAddOns || [];
  const addOnsCost = booking.addOnsCost || 0;
  const tollCharges = booking.tollCharges || 0;
  const nightCharges = booking.nightCharges || 0;
  const driverAllowance = booking.driverAllowance || 0;

  return (
    <div className="earnings-breakdown bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="earnings-header cursor-pointer p-4 hover:bg-gray-100 transition-colors flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" onClick={toggleExpand}>
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-semibold text-gray-800">Earnings Breakdown</h3>
          {!isRideCompleted && (
            <span className="text-xs text-gray-500 italic">Estimated earnings for this trip</span>
          )}
        </div>
        <button
          type="button"
          className="earnings-toggle text-gray-600 hover:text-gray-800 transition-colors flex items-center"
          aria-expanded={isExpanded}
          onClick={(e) => {
            e.stopPropagation();
            toggleExpand();
          }}
        >
          {isExpanded ? (
            <FaChevronUp className="w-4 h-4" />
          ) : (
            <FaChevronDown className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Main Breakdown */}
      <div className="earnings-main p-4 md:p-5 border-t border-gray-200">
        {!isExpanded ? (
          /* Collapsed State: Only show Your Earnings */
          <div className="earnings-total flex justify-between items-center">
            <span className="text-gray-800 font-semibold text-sm md:text-base">Your Earnings:</span>
            <span className="text-green-600 font-bold text-lg md:text-xl">₹{driverEarnings.toFixed(2)}</span>
          </div>
        ) : (
          /* Expanded State: Show full breakdown and services */
          <div className="space-y-2.5">
            {/* Total Fare */}
            <div className="earnings-item flex justify-between items-center text-sm md:text-base">
              <span className="text-gray-700 font-medium">Total Fare:</span>
              <span className="text-gray-900 font-semibold">₹{totalFare.toFixed(2)}</span>
            </div>

            {/* Admin Commission */}
            <div className="earnings-item flex justify-between items-center text-red-600 text-sm md:text-base">
              <span className="font-medium">Admin Commission ({commissionRate}%):</span>
              <span className="font-semibold">-₹{adminCommission.toFixed(2)}</span>
            </div>

            {/* GST */}
            <div className="earnings-item flex justify-between items-center text-red-600 text-sm md:text-base">
              <span className="font-medium">GST ({gstRate}%):</span>
              <span className="font-semibold">-₹{gstAmount.toFixed(2)}</span>
            </div>

            {/* Vendor Commission - Show only if > 0 */}
            {vendorCommission > 0 && (
              <div className="earnings-item flex justify-between items-center text-red-600 text-sm md:text-base">
                <span className="font-medium">Vendor Commission:</span>
                <span className="font-semibold">-₹{vendorCommission.toFixed(2)}</span>
              </div>
            )}

            {/* Your Earnings */}
            <div className="earnings-total flex justify-between items-center border-t border-gray-300 pt-2.5 mt-2.5">
              <span className="text-gray-800 font-semibold text-sm md:text-base">Your Earnings:</span>
              <span className="text-green-600 font-bold text-lg md:text-xl">₹{driverEarnings.toFixed(2)}</span>
            </div>

            {/* Services Included */}
            <div className="pt-3 border-t border-gray-200 mt-3 space-y-2.5">
              <h4 className="text-sm md:text-base font-semibold text-gray-700">Services Included</h4>

              {/* Toll Charges */}
              {tollCharges > 0 && (
                <div className="summary-item flex justify-between items-center text-green-600 text-sm md:text-base">
                  <span className="font-medium">Toll Charges:</span>
                  <span className="font-semibold">₹{tollCharges.toFixed(2)}</span>
                </div>
              )}

              {/* Night Charges */}
              {nightCharges > 0 && (
                <div className="summary-item flex justify-between items-center text-green-600 text-sm md:text-base">
                  <span className="font-medium">Night Charges:</span>
                  <span className="font-semibold">₹{nightCharges.toFixed(2)}</span>
                </div>
              )}

              {/* Driver Allowance */}
              {driverAllowance > 0 && (
                <div className="summary-item flex justify-between items-center text-green-600 text-sm md:text-base">
                  <span className="font-medium">Driver Allowance:</span>
                  <span className="font-semibold">₹{driverAllowance.toFixed(2)}</span>
                </div>
              )}

              {/* Add-ons Section */}
              {(selectedAddOns.length > 0 || addOnsCost > 0) && (
                <div className="addons-section space-y-2 pt-2 border-t border-gray-100">
                  {selectedAddOns.length > 0 ? (
                    selectedAddOns.map((addon, index) => (
                      <div key={index} className="addons-item flex justify-between items-center text-green-600 text-sm md:text-base">
                        <span className="font-medium">{addon.name}:</span>
                        <span className="font-semibold">₹{(addon.price || 0).toFixed(2)}</span>
                      </div>
                    ))
                  ) : addOnsCost > 0 ? (
                    <div className="addons-item flex justify-between items-center text-green-600 text-sm md:text-base">
                      <span className="font-medium">Add-ons:</span>
                      <span className="font-semibold">₹{addOnsCost.toFixed(2)}</span>
                    </div>
                  ) : null}
                </div>
              )}

              {/* No services message */}
              {tollCharges === 0 && nightCharges === 0 && driverAllowance === 0 && selectedAddOns.length === 0 && addOnsCost === 0 && (
                <p className="text-gray-500 text-sm italic">No additional services included in this ride</p>
              )}
            </div>

            {/* Journey Details & Calculations (only shown if completed details are provided) */}
            {(extraKm > 0 || waitingTime > 0 || booking.advancePaid > 0) && (
              <div className="bg-white border border-gray-200 p-4 rounded-xl space-y-3 mt-4 shadow-sm text-slate-700 text-[11px] sm:text-xs">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-gray-100 pb-1.5 flex items-center gap-1.5">
                  📏 Journey Details & Calculations
                </h4>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  {/* Distance Section */}
                  <div className="space-y-1.5 bg-gray-55/40 p-2.5 rounded-lg border border-gray-100">
                    <div className="flex justify-between items-center">
                      <span className="text-[9px] font-black text-slate-500 uppercase tracking-wider">Distance</span>
                      {extraKm > 0 ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded font-extrabold bg-orange-50 text-orange-700 border border-orange-100">
                          +{extraKm.toFixed(1)} km extra
                        </span>
                      ) : (
                        <span className="text-[9px] px-1.5 py-0.5 rounded font-extrabold bg-gray-100 text-gray-500 border border-gray-200">
                          Within limit
                        </span>
                      )}
                    </div>
                    
                    <div className="flex justify-between items-end">
                      <div>
                        <span className="text-xs font-bold text-slate-800 block">
                          Actual: {booking.distance || 0} km
                        </span>
                        <span className="text-[9px] text-slate-400 block">
                          Limit: {includedKm} km
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-black text-slate-800 block">
                          +₹{extraKmCharge.toFixed(2)}
                        </span>
                        <span className="text-[9px] text-slate-500 block">
                          @{extraFarePerKm}/km after limit
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Waiting Section */}
                  <div className="space-y-1.5 bg-gray-55/40 p-2.5 rounded-lg border border-gray-100">
                    <div className="flex justify-between items-center">
                      <span className="text-[9px] font-black text-slate-500 uppercase tracking-wider">Waiting Time</span>
                      {waitingTime > 0 ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded font-extrabold bg-orange-50 text-orange-700 border border-orange-100">
                          +{waitingTime} mins
                        </span>
                      ) : (
                        <span className="text-[9px] px-1.5 py-0.5 rounded font-extrabold bg-gray-100 text-gray-500 border border-gray-200">
                          No wait
                        </span>
                      )}
                    </div>

                    <div className="flex justify-between items-end">
                      <div>
                        <span className="text-xs font-bold text-slate-800 block">
                          Wait time: {waitingTime} mins
                        </span>
                        <span className="text-[9px] text-slate-400 block">
                          Rate: ₹{waitingChargePerMinute}/min
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-black text-slate-800 block">
                          +₹{waitingCharge.toFixed(2)}
                        </span>
                        <span className="text-[9px] text-slate-500 block">
                          total wait fee
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {booking.advancePaid > 0 && (
              <div className="bg-amber-500/5 border border-amber-500/10 p-3.5 rounded-xl space-y-2 mt-3 text-slate-700 text-[11px] sm:text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] px-2 py-0.5 rounded font-black bg-amber-100 text-amber-800 border border-amber-250">
                    ⚡ Advance Ride
                  </span>
                  <span className={`text-[10px] px-2.5 py-0.5 rounded font-black border ${
                    booking.paymentMethod?.toUpperCase() === "COD"
                      ? "bg-green-50 text-green-700 border-green-200"
                      : "bg-blue-50 text-blue-700 border-blue-200"
                  }`}>
                    {booking.paymentMethod?.toUpperCase() === "COD" ? "💵 COD" : "🔐 Online"}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs font-bold">
                  <span>Paid:</span>
                  <span className="text-emerald-600 font-extrabold">₹{booking.advancePaid}</span>
                </div>
                <div className="flex justify-between items-center text-xs font-bold">
                  <span>Due:</span>
                  <span className="text-rose-600 font-extrabold">
                      ₹{baseDue.toFixed(2)}
                  </span>
                </div>

                {/* Extra Distance in Advance Card */}
                {extraKm > 0 && (
                  <div className="border-t border-amber-500/10 pt-2 mt-1 space-y-0.5">
                    <div className="flex justify-between items-center text-xs font-bold">
                      <span className="text-amber-850 font-black">+{extraKm.toFixed(1)} km extra</span>
                      <span className="text-amber-850 font-extrabold">+₹{extraKmCharge.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-[10px] text-slate-500 font-bold">
                      <span>Actual: {booking.distance || 0} km | Limit: {includedKm} km</span>
                      <span>@{extraFarePerKm}/km after limit</span>
                    </div>
                  </div>
                )}

                {/* Waiting Time in Advance Card */}
                {waitingTime > 0 && (
                  <div className="border-t border-amber-500/10 pt-2 mt-1 space-y-0.5">
                    <div className="flex justify-between items-center text-xs font-bold">
                      <span className="text-amber-855 font-black">Waiting Time (+{waitingTime} mins)</span>
                      <span className="text-amber-855 font-extrabold">+₹{waitingCharge.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-[10px] text-slate-500 font-bold">
                      <span>Wait time: {waitingTime} mins | Rate: ₹{waitingChargePerMinute}/min</span>
                      <span>total wait fee</span>
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center text-xs font-extrabold border-t border-amber-500/15 pt-2 mt-1">
                  <span>Total Collect Amount:</span>
                  <span className="text-emerald-700 font-black text-sm">
                      ₹{totalCollectAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EarningsBreakdown;
